// LayoutEditor — desktop block list with @dnd-kit/sortable drag-reorder +
// inline BlockSettingsPanel accordion per block (per
// `docs/spec/wishlist/DESIGN.md §7`).
//
// W33.3 scope (this commit):
//   - Desktop drag-reorder via @dnd-kit/sortable (PointerSensor 3px)
//   - Inline BlockSettingsPanel accordion for each block
//   - Add-block dropdown (creates a new block of the chosen type; auto-
//     populates completion_sources per DATA_MODEL §5.5 for progress /
//     budget / checklist)
//   - Title-required validation (red border) for same-type-multi-block
//   - Explicit Save per panel (no auto-save per spec)
//   - Completion mode toggle (manual / aggregate) with per-source
//     checkboxes
//
// W34.2.3 follow-up: mobile path uses long-press → BottomSheet reorder
// (MobileBlockReorderSheet). dnd-kit sensors are gated on `!useIsMobile`
// so the desktop branch never receives touch events; mobile branch
// disables dnd-kit entirely.

import { useCallback, useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import {
  gapDown,
  POPOVER_PANEL_CLASS,
  POPOVER_ROW_CLASS,
} from "@/components/system/Popover";
import { BlockSettingsPanel } from "./BlockSettingsPanel";
import { MobileBlockReorderSheet } from "./MobileBlockReorderSheet";
import { useLongPress } from "@/lib/gestures/longPress";
import { useIsMobile } from "@/lib/hooks/useIsMobile";
import type { WishBlockOut, WishOut } from "@/lib/api/wishlist";

// Labels live in lib/wish/blockTypeLabel so the detail / settings surfaces stop
// printing the raw enum (AGENTS.md §3 #14). Re-exported here because the
// add-block chips are the canonical consumer.
import { ADDABLE_BLOCK_TYPES, blockTypeLabel } from "@/lib/wish/blockTypeLabel";

const PROGRESS_SOURCE_KINDS = new Set(["progress", "budget", "checklist"]);

export interface LayoutEditorProps {
  wish: WishOut;
  /** Save handler for a single block. */
  onSaveBlock: (input: {
    id: string;
    title: string;
    data: Record<string, unknown>;
  }) => void | Promise<void>;
  /** Add a new block of the given type. */
  onAddBlock: (input: {
    blockType: WishBlockOut["block_type"];
    title: string;
  }) => void | Promise<void>;
  /** Delete a block. */
  onDeleteBlock: (id: string) => void | Promise<void>;
  /** Reorder the block list (gap-based renumber per DATA_MODEL §8.4). */
  onReorder: (input: {
    items: { id: string; position: number }[];
  }) => void | Promise<void>;
  /** Set completion mode + per-source checkboxes. */
  onSetCompletion: (input: {
    completionMode: "manual" | "aggregate";
    manualPercent: number | null;
    completionSources: string[];
  }) => void | Promise<void>;
  /** Indicator for in-flight saves. */
  saving?: boolean;
  className?: string;
}

export function LayoutEditor({
  wish,
  onSaveBlock,
  onAddBlock,
  onDeleteBlock,
  onReorder,
  onSetCompletion,
  saving,
  className,
}: LayoutEditorProps) {
  const isMobile = useIsMobile();
  // Local optimistic order — server `position` is the source of truth,
  // but we keep the user's drag arrangement in this state for instant
  // feedback before the server round-trip.
  const [order, setOrder] = useState<string[]>(() =>
    [...wish.blocks].sort((a, b) => a.position - b.position).map((b) => b.id),
  );
  // Resync when the upstream wish changes (e.g. refetch after reorder).
  const wishBlockIds = useMemo(
    () =>
      [...wish.blocks].sort((a, b) => a.position - b.position).map((b) => b.id),
    [wish.blocks],
  );
  if (
    wishBlockIds.length !== order.length ||
    wishBlockIds.some((id, i) => id !== order[i])
  ) {
    setOrder(wishBlockIds);
  }

  // dnd-kit sensors only mount on desktop (per W34.2.3 anti-drift:
  // 不挂 sensor on mobile to avoid PointerSensor mistriggers).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (isMobile) return; // hard guard — mobile uses BottomSheet path
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = order.indexOf(String(active.id));
      const newIndex = order.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      const next = arrayMove(order, oldIndex, newIndex);
      setOrder(next);
      // Gap-based renumber (per DATA_MODEL §8.4): shift through a
      // 1,000,000 offset to dodge UNIQUE conflicts mid-update.
      const items = next.map((id, index) => ({
        id,
        position: 1_000_000 + index,
      }));
      void onReorder({ items: items as unknown as { id: string; position: number }[] });
    },
    [order, onReorder, isMobile],
  );

  const blocksById = useMemo(() => {
    const map = new Map<string, WishBlockOut>();
    for (const b of wish.blocks) map.set(b.id, b);
    return map;
  }, [wish.blocks]);

  const orderedBlocks = order
    .map((id) => blocksById.get(id))
    .filter((b): b is WishBlockOut => Boolean(b));

  // Count per type for the title-required rule (DATA_MODEL §8.3).
  const typeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const b of wish.blocks) {
      counts.set(b.block_type, (counts.get(b.block_type) ?? 0) + 1);
    }
    return counts;
  }, [wish.blocks]);

  // Mobile reorder sheet state.
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const openMobileReorder = useCallback(() => setMobileSheetOpen(true), []);
  const onMobileCommit = useCallback(
    async (orderedIds: string[]) => {
      const items = orderedIds.map((id, index) => ({
        id,
        position: 1_000_000 + index,
      }));
      await onReorder({ items });
    },
    [onReorder],
  );

  return (
    <section
      data-component="LayoutEditor"
      data-testid="layout-editor"
      data-viewport={isMobile ? "mobile" : "desktop"}
      className={cn("flex flex-col gap-s-3", className)}
    >
      {isMobile ? (
        <MobileBlockList
          orderedBlocks={orderedBlocks}
          typeCounts={typeCounts}
          saving={saving}
          onSaveBlock={onSaveBlock}
          onDeleteBlock={onDeleteBlock}
          onLongPressRow={openMobileReorder}
        />
      ) : (
        <BlockList
          orderedBlocks={orderedBlocks}
          typeCounts={typeCounts}
          sensors={sensors}
          onDragEnd={onDragEnd}
          saving={saving}
          onSaveBlock={onSaveBlock}
          onDeleteBlock={onDeleteBlock}
        />
      )}
      <AddBlockRow onAddBlock={onAddBlock} />
      <CompletionSettings
        wish={wish}
        onSetCompletion={onSetCompletion}
        saving={saving}
      />
      {isMobile ? (
        <MobileBlockReorderSheet
          blocks={orderedBlocks}
          open={mobileSheetOpen}
          onOpenChange={setMobileSheetOpen}
          onCommit={onMobileCommit}
        />
      ) : null}
    </section>
  );
}

// --- Mobile block list (long-press → reorder sheet) -------------------

function MobileBlockList({
  orderedBlocks,
  typeCounts,
  saving,
  onSaveBlock,
  onDeleteBlock,
  onLongPressRow,
}: {
  orderedBlocks: WishBlockOut[];
  typeCounts: Map<string, number>;
  saving?: boolean;
  onSaveBlock: (input: {
    id: string;
    title: string;
    data: Record<string, unknown>;
  }) => void | Promise<void>;
  onDeleteBlock: (id: string) => void | Promise<void>;
  onLongPressRow: () => void;
}) {
  if (orderedBlocks.length === 0) {
    return (
      <p
        data-testid="layout-editor-mobile-empty"
        className="text-body text-ink-3 py-s-6 text-center"
      >
        長按下方任一區塊可開啟排序介面。
      </p>
    );
  }
  return (
    <ol
      data-component="MobileBlockList"
      data-testid="layout-editor-mobile-block-list"
      className="flex flex-col gap-s-2"
    >
      {orderedBlocks.map((block) => (
        <MobileBlockItem
          key={block.id}
          block={block}
          typeCounts={typeCounts}
          saving={saving}
          onSaveBlock={onSaveBlock}
          onDeleteBlock={onDeleteBlock}
          onLongPressRow={onLongPressRow}
        />
      ))}
    </ol>
  );
}

function MobileBlockItem({
  block,
  typeCounts,
  saving,
  onSaveBlock,
  onDeleteBlock,
  onLongPressRow,
}: {
  block: WishBlockOut;
  typeCounts: Map<string, number>;
  saving?: boolean;
  onSaveBlock: (input: {
    id: string;
    title: string;
    data: Record<string, unknown>;
  }) => void | Promise<void>;
  onDeleteBlock: (id: string) => void | Promise<void>;
  onLongPressRow: () => void;
}) {
  // Per /impeccable 2026-09-22 audit: previously imported the legacy
  // useLongPress from MobileBlockReorderSheet, whose return value leaked a
  // `wasTriggered` callback as a DOM attribute when spread on <button>,
  // emitting "React does not recognize the `wasTriggered` prop" warnings.
  // Now using the canonical hook from @/lib/gestures/longPress.
  //
  // W39.93: the replacement was written `{...longPress}`. `useLongPress` returns
  // a single handler *function*, and spreading a function copies its own
  // enumerable properties — of which it has none — so the spread evaluated to
  // `{}` and no `onPointerDown` was ever attached. A real press-and-hold on a
  // block handle at 393 px opened nothing at all: the mobile block-reorder
  // sheet was unreachable by any gesture, with the hook sitting right there
  // looking correct. Pass the handler as the prop it is.
  const longPress = useLongPress({
    thresholdMs: 250,
    onLongPress: onLongPressRow,
  });
  const sameTypeCount = typeCounts.get(block.block_type) ?? 1;
  const titleRequired = sameTypeCount > 1;
  return (
    <li
      data-component="MobileSortableBlock"
      data-block-id={block.id}
      data-block-type={block.block_type}
      data-testid={`mobile-block-${block.id}`}
      className="flex flex-col gap-s-2"
    >
      <button
        type="button"
        onPointerDown={longPress}
        data-testid={`mobile-block-handle-${block.id}`}
        aria-label="長按開啟排序"
        // `min-h-11` — `py-s-2` alone made this 40px, measured at every viewport
        // in W46.0. It is a whole-row drag handle, so there is width to spare and
        // no neighbour to squeeze; the 4px of extra height is invisible.
        className="press flex min-h-11 items-center gap-s-2 px-s-3 py-s-2 rounded-full bg-paper-elevated border border-hairline text-ink-1"
      >
        <GripVertical className="h-4 w-4 text-ink-2" aria-hidden />
        <span className="text-meta text-ink-2 tabular-nums w-6 text-center">
          {block.position}
        </span>
        <span className="text-body-sm truncate flex-1 text-left">
          {block.title || <span className="text-ink-3 italic">未命名區塊</span>}
        </span>
        <span className="text-meta text-ink-3">長按排序</span>
      </button>
      <BlockSettingsPanel
        block={block}
        sameTypeCount={sameTypeCount}
        titleRequired={titleRequired}
        saving={saving}
        onSave={(input) => onSaveBlock({ id: block.id, ...input })}
        onDelete={() => onDeleteBlock(block.id)}
      />
    </li>
  );
}

// --- Block list with drag-reorder ---------------------------------------

function BlockList({
  orderedBlocks,
  typeCounts,
  sensors,
  onDragEnd,
  saving,
  onSaveBlock,
  onDeleteBlock,
}: {
  orderedBlocks: WishBlockOut[];
  typeCounts: Map<string, number>;
  sensors: ReturnType<typeof useSensors>;
  onDragEnd: (event: DragEndEvent) => void;
  saving?: boolean;
  onSaveBlock: (input: {
    id: string;
    title: string;
    data: Record<string, unknown>;
  }) => void | Promise<void>;
  onDeleteBlock: (id: string) => void | Promise<void>;
}) {
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={orderedBlocks.map((b) => b.id)}
        strategy={verticalListSortingStrategy}
      >
        <ol
          data-component="LayoutEditorBlockList"
          data-testid="layout-editor-block-list"
          className="flex flex-col gap-s-2"
        >
          {orderedBlocks.map((block) => (
            <SortableBlock
              key={block.id}
              block={block}
              typeCounts={typeCounts}
              saving={saving}
              onSaveBlock={onSaveBlock}
              onDeleteBlock={onDeleteBlock}
            />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
}

function SortableBlock({
  block,
  typeCounts,
  saving,
  onSaveBlock,
  onDeleteBlock,
}: {
  block: WishBlockOut;
  typeCounts: Map<string, number>;
  saving?: boolean;
  onSaveBlock: (input: {
    id: string;
    title: string;
    data: Record<string, unknown>;
  }) => void | Promise<void>;
  onDeleteBlock: (id: string) => void | Promise<void>;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });
  const sameTypeCount = typeCounts.get(block.block_type) ?? 1;
  const titleRequired = sameTypeCount > 1;
  return (
    <li
      ref={setNodeRef}
      data-component="SortableBlock"
      data-block-id={block.id}
      data-block-type={block.block_type}
      data-testid={`sortable-block-${block.id}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
      className="flex flex-col gap-s-2"
    >
      <div className="flex items-center gap-s-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label="拖曳排序"
          data-testid={`sortable-block-handle-${block.id}`}
          className="press min-h-11 min-w-11 flex items-center justify-center rounded-md text-ink-2 hover:bg-paper-deep cursor-grab"
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </button>
        <span className="text-meta text-ink-3 tabular-nums w-6 text-center">
          {block.position}
        </span>
        <span className="text-meta text-ink-2 truncate flex-1">
          {block.title}
        </span>
      </div>
      <BlockSettingsPanel
        block={block}
        sameTypeCount={sameTypeCount}
        titleRequired={titleRequired}
        saving={saving}
        onSave={(input) => onSaveBlock({ id: block.id, ...input })}
        onDelete={() => onDeleteBlock(block.id)}
      />
    </li>
  );
}

// --- Add block dropdown -------------------------------------------------

function AddBlockRow({
  onAddBlock,
}: {
  onAddBlock: (input: {
    blockType: WishBlockOut["block_type"];
    title: string;
  }) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div data-component="AddBlockRow" className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        data-testid="add-block-toggle"
        className="press w-full min-h-11 flex items-center justify-center gap-2 rounded-full border border-hairline bg-paper-elevated text-ink-1 text-body-sm font-semibold"
      >
        <Plus className="h-4 w-4" aria-hidden />
        新增區塊
        <ChevronDown
          className={cn(
            "h-4 w-4 transition-transform",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      {open ? (
        <ul
          role="menu"
          data-testid="add-block-menu"
          style={gapDown()}
          // W52.3 — the shared panel and gap. This one is a **full-width**
          // dropdown under a full-width trigger, so "aligned to the trigger" is
          // satisfied by being full width; it does not take a placement hook,
          // and the guard's exception for that is written there.
          className={cn(
            "absolute z-10 w-full",
            POPOVER_PANEL_CLASS,
            /* gap via inline style — see `gapDown` */
            "origin-top",
          )}
        >
          {ADDABLE_BLOCK_TYPES.map((opt) => (
            <li key={opt.value} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  void onAddBlock({
                    blockType: opt.value,
                    title: defaultTitleFor(opt.value),
                  });
                }}
                data-testid={`add-block-${opt.value}`}
                className={POPOVER_ROW_CLASS}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function defaultTitleFor(blockType: string): string {
  switch (blockType) {
    case "progress":
      return "進度";
    case "budget":
      return "預算";
    case "checklist":
      return "待辦清單";
    case "photo":
      return "照片";
    case "link":
      return "連結";
    case "notes":
      return "備忘";
    default:
      return blockType;
  }
}

// --- Completion settings (mode toggle + sources) -----------------------

function CompletionSettings({
  wish,
  onSetCompletion,
  saving,
}: {
  wish: WishOut;
  onSetCompletion: (input: {
    completionMode: "manual" | "aggregate";
    manualPercent: number | null;
    completionSources: string[];
  }) => void | Promise<void>;
  saving?: boolean;
}) {
  const aggregateSources = wish.blocks
    .filter((b) => PROGRESS_SOURCE_KINDS.has(b.block_type))
    .map((b) => `${b.block_type}:${b.id}`);
  return (
    <section
      data-component="CompletionSettings"
      data-testid="completion-settings"
      className="bg-paper-elevated border border-hairline rounded-2xl p-s-4 flex flex-col gap-s-3"
    >
      <h3 className="font-display text-body font-semibold text-ink-1">
        完成度設定
      </h3>
      <fieldset
        data-component="CompletionModeFieldset"
        className="flex flex-col gap-s-2"
      >
        <legend className="sr-only">完成度模式</legend>
          {/* `min-h-11 -my-s-1` — the label is the real tap target, and it was
              only as tall as its 13px radio plus one line of text (~24px), so
              the row's own height was the floor. The negative margin takes back
              the padding's contribution to the stack, which keeps the fieldset's
              `gap-s-2` rhythm exactly where it was; only the live area grew.
              Measured 13×13 for the inputs and under 44 for the rows (W46.0). */}
          <label className="press flex min-h-11 -my-s-1 items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="completion-mode"
              value="aggregate"
              checked={wish.completion_mode === "aggregate"}
              onChange={() =>
                onSetCompletion({
                  completionMode: "aggregate",
                  manualPercent: null,
                  completionSources: aggregateSources,
                })
              }
              data-testid="completion-mode-aggregate"
              className="accent-ink-1"
            />
            <span className="text-body-sm text-ink-1">
              自動（從以下來源平均）
            </span>
          </label>
          <label className="press flex min-h-11 -my-s-1 items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="completion-mode"
              value="manual"
              checked={wish.completion_mode === "manual"}
              onChange={() =>
                onSetCompletion({
                  completionMode: "manual",
                  manualPercent: wish.manual_percent ?? 0,
                  completionSources: [],
                })
              }
              data-testid="completion-mode-manual"
              className="accent-ink-1"
            />
            <span className="text-body-sm text-ink-1">手動設定</span>
          </label>
      </fieldset>
      {wish.completion_mode === "aggregate" ? (
        <ul
          data-component="CompletionSourcesList"
          data-testid="completion-sources-list"
          className="flex flex-col gap-s-1 pl-6"
        >
          {wish.blocks
            .filter((b) => PROGRESS_SOURCE_KINDS.has(b.block_type))
            .map((b) => {
              const source = `${b.block_type}:${b.id}`;
              const checked = wish.completion_sources.includes(source);
              return (
                <li
                  key={b.id}
                  data-component="CompletionSourceItem"
                  className="flex items-center gap-2"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={saving}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? Array.from(new Set([...wish.completion_sources, source]))
                        : wish.completion_sources.filter((s) => s !== source);
                      onSetCompletion({
                        completionMode: "aggregate",
                        manualPercent: null,
                        completionSources: next,
                      });
                    }}
                    data-testid={`completion-source-${b.id}`}
                    className="accent-ink-1"
                  />
                  <span className="text-meta text-ink-2 truncate">
                    {b.title || blockTypeLabel(b.block_type)}（{blockTypeLabel(b.block_type)}）
                  </span>
                </li>
              );
            })}
        </ul>
      ) : (
        <div
          data-component="CompletionManualPercent"
          className="flex items-center gap-2 pl-6"
        >
          <input
            type="number"
            min={0}
            max={100}
            value={wish.manual_percent ?? 0}
            onChange={(e) =>
              onSetCompletion({
                completionMode: "manual",
                manualPercent: Number(e.target.value),
                completionSources: [],
              })
            }
            disabled={saving}
            data-testid="completion-manual-percent"
            className="w-20 h-10 px-3 rounded-md border border-hairline bg-paper-elevated text-ink-1"
          />
          <span className="text-meta text-ink-3">%</span>
        </div>
      )}
    </section>
  );
}
